/**
 * R2 Measurement Engine — Persistent Store
 * Full Zustand + localStorage persistence for cross-page, cross-session
 * measurement memory with collections, layers, scale calibration, undo/redo.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

// ── Enums & Types ─────────────────────────────────────────────────────────────
export type MeasurementType =
  | 'distance' | 'polyline' | 'area' | 'radius' | 'diameter'
  | 'angle' | 'arc' | 'elevation' | 'slope' | 'chain' | 'running';

export type MeasurementUnit = 'px' | 'mm' | 'm' | 'ft' | 'in';

export const BUILT_IN_LAYERS = [
  'Telecom', 'Electrical', 'Mechanical', 'Architectural',
  'Structural', 'Civil', 'Fire Alarm', 'Security', 'Notes',
] as const;

export const LAYER_COLORS: Record<string, string> = {
  Telecom:      '#5eead4',
  Electrical:   '#fbbf24',
  Mechanical:   '#a78bfa',
  Architectural:'#60a5fa',
  Structural:   '#f87171',
  Civil:        '#94a3b8',
  'Fire Alarm': '#fb923c',
  Security:     '#4ade80',
  Notes:        '#e2ecfa',
  Default:      '#5eead4',
};

export interface MeasurementStyle {
  color: string;
  lineWidth: number;
  arrowType: 'none' | 'arrow' | 'tick' | 'dot';
  fontSize: number;
  showBackground: boolean;
  opacity: number;
  dashArray: string;
}

export interface MeasurementPoint { x: number; y: number; }

export interface Measurement {
  id: string;
  type: MeasurementType;
  points: MeasurementPoint[];
  value: number;         // raw pixel value
  calibratedValue: number | null; // in real units (null if not calibrated)
  unit: MeasurementUnit;
  label: string;
  layer: string;
  collection: string;
  page: string;          // route name when measured
  pageTitle: string;
  style: MeasurementStyle;
  notes: string;
  timestamp: string;
  calibrated: boolean;
}

export interface MeasurementCollection {
  id: string;
  name: string;
  color: string;
  layer: string;
  measurements: string[]; // measurement IDs
  createdAt: string;
}

export interface ScaleCalibration {
  page: string;
  p1: MeasurementPoint;
  p2: MeasurementPoint;
  pixelDist: number;
  realDist: number;
  unit: MeasurementUnit;
  pxPerUnit: number;
  label: string;         // e.g. "1:100" or "3/16\"=1'"
}

export type ActiveTool = MeasurementType | 'dimension' | 'calibrate' | null;
export type SnapType = 'endpoint' | 'midpoint' | 'center' | 'intersection' | 'grid' | 'free';

export interface SnapPoint { point: MeasurementPoint; type: SnapType; }

// ── Default style ─────────────────────────────────────────────────────────────
function defaultStyle(layer = 'Default'): MeasurementStyle {
  return {
    color: LAYER_COLORS[layer] ?? '#5eead4',
    lineWidth: 1.5,
    arrowType: 'arrow',
    fontSize: 11,
    showBackground: true,
    opacity: 0.92,
    dashArray: 'none',
  };
}

// ── Store interface ───────────────────────────────────────────────────────────
interface MeasurementState {
  // Data
  measurements: Measurement[];
  collections: MeasurementCollection[];
  calibrations: Record<string, ScaleCalibration>;

  // UI state (not persisted)
  activeTool: ActiveTool;
  overlayActive: boolean;
  activeLayer: string;
  activeCollection: string | null;
  activeUnit: MeasurementUnit;
  currentPage: string;
  pendingPoints: MeasurementPoint[];
  hoveredId: string | null;
  snapPoint: SnapPoint | null;

  // Clipboard
  lastValue: string;

  // Undo/Redo
  past: Measurement[][];
  future: Measurement[][];

  // Actions
  setOverlayActive: (v: boolean) => void;
  setActiveTool: (t: ActiveTool) => void;
  setActiveLayer: (l: string) => void;
  setActiveCollection: (id: string | null) => void;
  setActiveUnit: (u: MeasurementUnit) => void;
  setCurrentPage: (p: string) => void;
  setPendingPoints: (pts: MeasurementPoint[]) => void;
  addPendingPoint: (pt: MeasurementPoint) => void;
  setHovered: (id: string | null) => void;
  setSnapPoint: (s: SnapPoint | null) => void;

  addMeasurement: (m: Measurement) => void;
  updateMeasurement: (id: string, patch: Partial<Measurement>) => void;
  deleteMeasurement: (id: string) => void;
  clearPage: (page: string) => void;

  addCollection: (name: string, layer?: string, color?: string) => string;
  deleteCollection: (id: string) => void;
  addToCollection: (collId: string, measId: string) => void;
  removeFromCollection: (collId: string, measId: string) => void;

  setCalibration: (page: string, cal: ScaleCalibration) => void;
  getCalibration: (page: string) => ScaleCalibration | null;
  convertToReal: (px: number, page: string) => { value: number; unit: MeasurementUnit } | null;

  undo: () => void;
  redo: () => void;
  pushUndo: () => void;

  setLastValue: (v: string) => void;
  copyToClipboard: (value: string) => void;

  // Selectors (computed)
  getMeasurementsForPage: (page: string) => Measurement[];
  getMeasurementsForCollection: (collId: string) => Measurement[];
  getCollectionTotal: (collId: string) => number;
  getLayerTotal: (layer: string) => number;
}

// ── Store implementation ──────────────────────────────────────────────────────
export const useR2MeasurementStore = create<MeasurementState>()(
  persist(
    immer((set, get) => ({
      measurements: [],
      collections: [],
      calibrations: {},
      activeTool: null,
      overlayActive: false,
      activeLayer: 'Telecom',
      activeCollection: null,
      activeUnit: 'px',
      currentPage: '',
      pendingPoints: [],
      hoveredId: null,
      snapPoint: null,
      lastValue: '',
      past: [],
      future: [],

      setOverlayActive: (v) => set((s) => { s.overlayActive = v; if (!v) { s.pendingPoints = []; s.activeTool = null; } }),
      setActiveTool: (t) => set((s) => { s.activeTool = t; s.pendingPoints = []; }),
      setActiveLayer: (l) => set((s) => { s.activeLayer = l; }),
      setActiveCollection: (id) => set((s) => { s.activeCollection = id; }),
      setActiveUnit: (u) => set((s) => { s.activeUnit = u; }),
      setCurrentPage: (p) => set((s) => { s.currentPage = p; }),
      setPendingPoints: (pts) => set((s) => { s.pendingPoints = pts; }),
      addPendingPoint: (pt) => set((s) => { s.pendingPoints.push(pt); }),
      setHovered: (id) => set((s) => { s.hoveredId = id; }),
      setSnapPoint: (sp) => set((s) => { s.snapPoint = sp; }),

      addMeasurement: (m) => {
        get().pushUndo();
        set((s) => {
          s.measurements.push(m);
          // Auto-add to active collection
          if (s.activeCollection) {
            const col = s.collections.find((c) => c.id === s.activeCollection);
            if (col && !col.measurements.includes(m.id)) col.measurements.push(m.id);
          }
          s.pendingPoints = [];
          s.lastValue = formatValue(m.calibratedValue ?? m.value, m.unit);
        });
        // Copy to clipboard
        get().copyToClipboard(formatValue(m.calibratedValue ?? m.value, m.unit));
      },

      updateMeasurement: (id, patch) => set((s) => {
        const m = s.measurements.find((x) => x.id === id);
        if (m) Object.assign(m, patch);
      }),

      deleteMeasurement: (id) => {
        get().pushUndo();
        set((s) => {
          s.measurements = s.measurements.filter((m) => m.id !== id);
          s.collections.forEach((c) => { c.measurements = c.measurements.filter((x) => x !== id); });
        });
      },

      clearPage: (page) => {
        get().pushUndo();
        set((s) => {
          const ids = new Set(s.measurements.filter((m) => m.page === page).map((m) => m.id));
          s.measurements = s.measurements.filter((m) => m.page !== page);
          s.collections.forEach((c) => { c.measurements = c.measurements.filter((x) => !ids.has(x)); });
        });
      },

      addCollection: (name, layer = 'Telecom', color) => {
        const id = crypto.randomUUID();
        const col: MeasurementCollection = {
          id, name, layer,
          color: color ?? (LAYER_COLORS[layer] ?? '#5eead4'),
          measurements: [],
          createdAt: new Date().toISOString(),
        };
        set((s) => { s.collections.push(col); });
        return id;
      },

      deleteCollection: (id) => set((s) => {
        s.collections = s.collections.filter((c) => c.id !== id);
        if (s.activeCollection === id) s.activeCollection = null;
      }),

      addToCollection: (collId, measId) => set((s) => {
        const c = s.collections.find((x) => x.id === collId);
        if (c && !c.measurements.includes(measId)) c.measurements.push(measId);
      }),

      removeFromCollection: (collId, measId) => set((s) => {
        const c = s.collections.find((x) => x.id === collId);
        if (c) c.measurements = c.measurements.filter((x) => x !== measId);
      }),

      setCalibration: (page, cal) => set((s) => { s.calibrations[page] = cal; s.activeUnit = cal.unit; }),
      getCalibration: (page) => get().calibrations[page] ?? null,

      convertToReal: (px, page) => {
        const cal = get().calibrations[page];
        if (!cal) return null;
        return { value: px / cal.pxPerUnit, unit: cal.unit };
      },

      pushUndo: () => set((s) => {
        s.past = [...s.past.slice(-49), JSON.parse(JSON.stringify(s.measurements))];
        s.future = [];
      }),

      undo: () => set((s) => {
        if (s.past.length === 0) return;
        s.future = [JSON.parse(JSON.stringify(s.measurements)), ...s.future.slice(0, 49)];
        s.measurements = s.past[s.past.length - 1] ?? [];
        s.past = s.past.slice(0, -1);
      }),

      redo: () => set((s) => {
        if (s.future.length === 0) return;
        s.past = [...s.past, JSON.parse(JSON.stringify(s.measurements))];
        s.measurements = s.future[0] ?? [];
        s.future = s.future.slice(1);
      }),

      setLastValue: (v) => set((s) => { s.lastValue = v; }),

      copyToClipboard: (value) => {
        try { navigator.clipboard.writeText(value); } catch { /* noop */ }
        set((s) => { s.lastValue = value; });
      },

      // Computed selectors
      getMeasurementsForPage: (page) => get().measurements.filter((m) => m.page === page),
      getMeasurementsForCollection: (collId) => {
        const col = get().collections.find((c) => c.id === collId);
        if (!col) return [];
        return get().measurements.filter((m) => col.measurements.includes(m.id));
      },
      getCollectionTotal: (collId) => {
        const meas = get().getMeasurementsForCollection(collId);
        return meas.reduce((sum, m) => sum + (m.calibratedValue ?? m.value), 0);
      },
      getLayerTotal: (layer) => {
        return get().measurements
          .filter((m) => m.layer === layer)
          .reduce((sum, m) => sum + (m.calibratedValue ?? m.value), 0);
      },
    })),
    {
      name: 'r2:measurement-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist data, not UI state
      partialize: (s) => ({
        measurements: s.measurements,
        collections: s.collections,
        calibrations: s.calibrations,
        activeLayer: s.activeLayer,
        activeUnit: s.activeUnit,
        lastValue: s.lastValue,
      }),
    },
  ),
);

// ── Helpers ───────────────────────────────────────────────────────────────────
export function formatValue(val: number, unit: MeasurementUnit, decimals = 2): string {
  const v = val.toFixed(decimals);
  if (unit === 'px') return `${v} px`;
  if (unit === 'mm') return `${v} mm`;
  if (unit === 'm')  return `${v} m`;
  if (unit === 'ft') return `${v} ft`;
  if (unit === 'in') return `${v}"`;
  return `${v}`;
}

export function pointDist(a: MeasurementPoint, b: MeasurementPoint): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

export function polylineLength(pts: MeasurementPoint[]): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += pointDist(pts[i - 1]!, pts[i]!);
  return total;
}

export function polygonArea(pts: MeasurementPoint[]): number {
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    area += (pts[i]?.x ?? 0) * (pts[j]?.y ?? 0) - (pts[j]?.x ?? 0) * (pts[i]?.y ?? 0);
  }
  return Math.abs(area) / 2;
}

export function angleBetween(a: MeasurementPoint, vertex: MeasurementPoint, b: MeasurementPoint): number {
  const vax = a.x - vertex.x, vay = a.y - vertex.y;
  const vbx = b.x - vertex.x, vby = b.y - vertex.y;
  const dot  = vax * vbx + vay * vby;
  const mag  = Math.sqrt(vax ** 2 + vay ** 2) * Math.sqrt(vbx ** 2 + vby ** 2);
  if (mag === 0) return 0;
  return (Math.acos(Math.min(1, Math.max(-1, dot / mag))) * 180) / Math.PI;
}

export function makeId(): string {
  return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
}

export function defaultMeasurementStyle(layer: string): MeasurementStyle {
  return defaultStyle(layer);
}
